import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";

interface SlackWorkspace {
  workspace_id: string;
  team_name: string;
  team_id: string;
  created_at: string;
}

export function Integrations() {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [slackWorkspace, setSlackWorkspace] = useState<SlackWorkspace | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    // Check if this is a redirect back from Slack OAuth
    const params = new URLSearchParams(location.search);
    if (params.get('slack_connected') === 'true') {
      toast.success('Slack workspace connected successfully!');
    }
    
    const fetchSlackWorkspace = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching organization for user:', user.id);
        
        // First get the user's organization ID
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
          
        if (userError) {
          console.error('Error fetching user organization:', userError);
          setError(`Failed to fetch user's organization: ${userError.message}`);
          throw userError;
        }
        
        if (!userData || !userData.organization_id) {
          console.error('User has no organization_id', userData);
          setError('Your user account is not associated with an organization');
          throw new Error('No organization_id found for user');
        }
        
        console.log('Found organization_id:', userData.organization_id);
        
        // Then get the Slack workspace for this organization
        const { data: workspaceData, error: workspaceError } = await supabase
          .from('slack_workspaces')
          .select('workspace_id, team_name, team_id, created_at')
          .eq('organization_id', userData.organization_id)
          .maybeSingle();
          
        if (workspaceError) {
          console.error('Error fetching Slack workspace:', workspaceError);
          setError(`Failed to fetch Slack workspace: ${workspaceError.message}`);
          throw workspaceError;
        }
        
        console.log('Slack workspace data:', workspaceData);
        setSlackWorkspace(workspaceData);
      } catch (error) {
        console.error('Error fetching Slack workspace:', error);
        // Don't show a toast here as we already set the error state
      } finally {
        setLoading(false);
      }
    };
    
    fetchSlackWorkspace();
  }, [user, location.search]);
  
  const handleConnectSlack = () => {
    if (!user) {
      toast.error("You must be logged in to connect to Slack");
      return;
    }
    
    try {
      // Create a state parameter with user ID and return path
      const stateData = {
        userId: user.id,
        returnTo: '/settings?tab=integrations'
      };
      const stateParam = btoa(JSON.stringify(stateData));
      
      // Use the Edge Function endpoint from the environment variable
      const supabaseFunctionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || 
        'https://gdpicetapcjfiosrgoky.supabase.co/functions/v1';
        
      const redirectUrl = `${supabaseFunctionsUrl}/slack-oauth/authorize?state=${encodeURIComponent(stateParam)}`;
      
      console.log('Redirecting to Slack OAuth:', redirectUrl);
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Error preparing Slack OAuth redirect:', error);
      toast.error('Failed to prepare Slack connection');
    }
  };
  
  const handleDisconnectSlack = async () => {
    if (!slackWorkspace) return;
    
    try {
      const { error } = await supabase
        .from('slack_workspaces')
        .delete()
        .eq('workspace_id', slackWorkspace.workspace_id);
        
      if (error) throw error;
      
      setSlackWorkspace(null);
      toast.success('Slack workspace disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting Slack workspace:', error);
      toast.error('Failed to disconnect Slack workspace');
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Slack Integration</CardTitle>
          <CardDescription>
            Connect your Slack workspace to enable notifications and commands
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="p-4 bg-destructive/10 text-destructive rounded-lg mb-4">
              <p className="font-medium">Error fetching Slack workspace</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : null}
          
          {slackWorkspace ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-accent/40 rounded-lg">
                <div>
                  <p className="font-medium">{slackWorkspace.team_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Connected since {new Date(slackWorkspace.created_at).toLocaleDateString()}
                  </p>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={handleDisconnectSlack}
                >
                  Disconnect
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Your Slack workspace is connected. You can now receive notifications and use Slack commands.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <Button onClick={handleConnectSlack}>
                  Connect Slack Workspace
                </Button>
              </div>
              <p className="text-sm text-muted-foreground text-center">
                Connect your Slack workspace to enable notifications and use Slack commands with your team.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 